import type {
  SessionRetention,
  RetentionStatus,
} from "../../../../shared/analysis-types";
import { CircleCheck, CircleAlert, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";

const statusConfig: Record<
  RetentionStatus,
  {
    label: string;
    Icon: typeof CircleCheck;
    containerClass: string;
    accentClass: string;
  }
> = {
  confirmed: {
    label: "Next session confirmed",
    Icon: CircleCheck,
    containerClass: "bg-success-soft border-forest-100 border-l-success",
    accentClass: "text-success",
  },
  absent: {
    label: "No next session confirmed",
    Icon: CircleAlert,
    containerClass: "bg-danger-soft border-error-light border-l-destructive",
    accentClass: "text-destructive",
  },
  unconfirmed: {
    label: "Next session discussed but not confirmed",
    Icon: TriangleAlert,
    containerClass: "bg-warning-soft border-warning-soft border-l-warning",
    accentClass: "text-warning",
  },
};

interface RetentionCalloutProps {
  retention: SessionRetention;
  className?: string;
}

export function RetentionCallout({ retention, className }: RetentionCalloutProps) {
  const config = statusConfig[retention.status];
  const { Icon } = config;

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-xl border border-l-4 p-4",
        config.containerClass,
        className
      )}
    >
      <Icon className={cn("h-8 w-8 shrink-0", config.accentClass)} />
      <div className="flex flex-col gap-1">
        <span
          className={cn(
            "text-xs font-bold uppercase tracking-wider",
            config.accentClass
          )}
        >
          Retention
        </span>
        <span className={cn("text-sm font-semibold", config.accentClass)}>
          {config.label}
        </span>
        <p className="text-sm leading-relaxed text-foreground">{retention.text}</p>
      </div>
    </div>
  );
}
